import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Stethoscope, FileText, Activity, AlertCircle, TestTube, Heart, Brain, Eye, Ear, Wind, ChevronDown, ChevronUp, Download, ExternalLink, ClipboardList, Pill, AlertTriangle, Scissors } from 'lucide-react';
import { cn } from '@/lib/utils';

// Render HTML content safely with proper formatting
function renderHtml(html: string | undefined): string {
  if (!html) return '';
  // If it already has HTML tags, return as-is for dangerouslySetInnerHTML
  if (/<[a-z][\s\S]*>/i.test(html)) {
    return html;
  }
  // Otherwise convert newlines to <br> for plain text
  return html.replace(/\n/g, '<br>');
}

// Strip HTML tags for plain text display
function stripHtml(html: string | undefined): string {
  if (!html) return '';
  return html
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<ul>/gi, '')
    .replace(/<\/ul>/gi, '')
    .replace(/<ol>/gi, '')
    .replace(/<\/ol>/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
}

// Physical Assessment type
interface PhysicalAssessment {
  type: string;
  findings: string;
}

// Helper to detect if a string is a UUID
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Get display name for assessment type (handles legacy UUIDs)
function getAssessmentDisplayName(type: string, index: number): string {
  if (isUUID(type)) {
    // Legacy data with UUID - show generic name with number
    return `System Examination ${index + 1}`;
  }
  return type;
}

// Diagnostic Test type
interface DiagnosticTest {
  name: string;
  file?: string;
  fileUrl?: string;
  description: string;
  results?: string;
  testDate?: string;
  unit?: string;
  referenceRange?: string;
  value?: string;
}

// Icon mapping for body systems
const systemIcons: Record<string, React.ReactNode> = {
  'general_constitutional': <ClipboardList className="w-5 h-5" />,
  'general': <ClipboardList className="w-5 h-5" />,
  'heent': <Ear className="w-5 h-5" />,
  'ent': <Ear className="w-5 h-5" />,
  'neck': <Stethoscope className="w-5 h-5" />,
  'cardiovascular': <Heart className="w-5 h-5" />,
  'respiratory': <Wind className="w-5 h-5" />,
  'abdominal_gi': <Activity className="w-5 h-5" />,
  'gastrointestinal': <Activity className="w-5 h-5" />,
  'musculoskeletal': <Stethoscope className="w-5 h-5" />,
  'neurological': <Brain className="w-5 h-5" />,
  'skin': <Eye className="w-5 h-5" />,
  'dermatological': <Eye className="w-5 h-5" />,
  'psych_mental': <Brain className="w-5 h-5" />,
  'genitourinary': <Activity className="w-5 h-5" />,
  'vital signs': <Activity className="w-5 h-5" />,
};

function getSystemIcon(type: string): React.ReactNode {
  const key = type.toLowerCase();
  for (const [system, icon] of Object.entries(systemIcons)) {
    if (key.includes(system)) return icon;
  }
  return <Stethoscope className="w-5 h-5" />;
}

// ============================================
// PRESENTING COMPLAINT TAB
// ============================================
export function PresentingComplaintTab({ data }: { data?: string }) {
  const hasData = data && stripHtml(data).length > 0;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-xl font-semibold">Presenting Complaint</h3>
      </div>
      
      <Card className="p-6 bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800">
        {hasData ? (
          <div 
            className="prose prose-sm max-w-none dark:prose-invert text-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderHtml(data) }}
          />
        ) : (
          <p className="text-muted-foreground italic">
            No presenting complaint information available. This will be revealed during the consultation.
          </p>
        )}
      </Card>
      
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          💡 <strong>Tip:</strong> Start by asking open-ended questions about the patient's main concern and how it's affecting their daily life.
        </p>
      </div>
    </div>
  );
}

// ============================================
// MEDICAL HISTORY TAB
// ============================================
interface MedicalHistoryData {
  pastMedicalHistory?: string;
  surgicalHistory?: string;
  medications?: string;
  allergies?: string;
  familyHistory?: string;
  socialHistory?: string;
}

export function MedicalHistoryTab({ 
  data,
  structuredData 
}: { 
  data?: string;
  structuredData?: MedicalHistoryData;
}) {
  const hasMainData = data && stripHtml(data).length > 0;
  
  // Parse structured data if provided
  const sections = [
    { key: 'pastMedicalHistory', label: 'Past Medical History', icon: FileText, color: 'blue' },
    { key: 'surgicalHistory', label: 'Surgical History', icon: Scissors, color: 'purple' },
    { key: 'medications', label: 'Medications', icon: Pill, color: 'green' },
    { key: 'allergies', label: 'Allergies', icon: AlertTriangle, color: 'red' },
    { key: 'familyHistory', label: 'Family History', icon: Heart, color: 'pink' },
    { key: 'socialHistory', label: 'Social History', icon: Activity, color: 'orange' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold">Medical History</h3>
      </div>
      
      {/* Main medical history text */}
      {hasMainData && (
        <Card className="p-6 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
          <div 
            className="prose prose-sm max-w-none dark:prose-invert text-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderHtml(data) }}
          />
        </Card>
      )}

      {/* Structured sections */}
      {structuredData && Object.keys(structuredData).some(k => structuredData[k as keyof MedicalHistoryData]) ? (
        <div className="grid md:grid-cols-2 gap-4">
          {sections.map(({ key, label, icon: Icon, color }) => {
            const value = structuredData[key as keyof MedicalHistoryData];
            if (!value) return null;
            return (
              <Card key={key} className={cn(
                "p-4 border-l-4",
                color === 'blue' && "border-l-blue-500 bg-blue-50/30 dark:bg-blue-900/10",
                color === 'purple' && "border-l-purple-500 bg-purple-50/30 dark:bg-purple-900/10",
                color === 'green' && "border-l-green-500 bg-green-50/30 dark:bg-green-900/10",
                color === 'red' && "border-l-red-500 bg-red-50/30 dark:bg-red-900/10",
                color === 'pink' && "border-l-pink-500 bg-pink-50/30 dark:bg-pink-900/10",
                color === 'orange' && "border-l-orange-500 bg-orange-50/30 dark:bg-orange-900/10",
              )}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">{label}</h4>
                </div>
                <div 
                  className="text-sm prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: renderHtml(value) }}
                />
              </Card>
            );
          })}
        </div>
      ) : !hasMainData && (
        <>
          <Card className="p-6 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
            <p className="text-muted-foreground italic">
              Medical history details will be gathered during the consultation. Ask about past medical conditions, surgeries, and hospitalizations.
            </p>
          </Card>
          <div className="grid md:grid-cols-2 gap-4">
            {sections.slice(0, 4).map(({ key, label }) => (
              <Card key={key} className="p-4 border-dashed">
                <h4 className="font-medium text-sm text-muted-foreground mb-2">{label}</h4>
                <p className="text-sm text-muted-foreground/70">To be elicited during consultation</p>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================
// PHYSICAL ASSESSMENT TAB
// ============================================
export function PhysicalAssessmentTab({ 
  data,
  assessments,
  afterConsultation = false
}: { 
  data?: string;
  assessments?: PhysicalAssessment[];
  afterConsultation?: boolean;
}) {
  const [expandedSystems, setExpandedSystems] = useState<string[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<'vitals' | 'systems' | 'investigations'>('vitals');

  const hasTextData = data && stripHtml(data).length > 0;
  const hasAssessments = assessments && assessments.length > 0;

  // Group assessments by category
  const vitalSigns = assessments?.filter(a => 
    a.type.toLowerCase().includes('vital') || 
    a.type.toLowerCase().includes('blood pressure') ||
    a.type.toLowerCase().includes('heart rate') ||
    a.type.toLowerCase().includes('temperature') ||
    a.type.toLowerCase().includes('respiratory rate') ||
    a.type.toLowerCase().includes('oxygen') ||
    a.type.toLowerCase().includes('spo2')
  ) || [];

  const systemExams = assessments?.filter(a => 
    !vitalSigns.includes(a) && 
    !a.type.toLowerCase().includes('investigation') &&
    !a.type.toLowerCase().includes('test')
  ) || [];

  const toggleSystem = (type: string) => {
    setExpandedSystems(prev => 
      prev.includes(type) 
        ? prev.filter(s => s !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
          <Stethoscope className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold">Physical Assessment</h3>
        {afterConsultation && (
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            Revealed after consultation
          </Badge>
        )}
      </div>
      
      {/* Main text data if exists */}
      {hasTextData && (
        <Card className="p-6 bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800">
          <div 
            className="prose prose-sm max-w-none dark:prose-invert text-foreground leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderHtml(data) }}
          />
        </Card>
      )}

      {/* Reminder */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          🩺 <strong>Remember:</strong> Explain each examination step to the patient and obtain consent before proceeding.
        </p>
      </div>

      {/* Sub-tabs for Physical Assessment sections */}
      {hasAssessments ? (
        <Tabs value={activeSubTab} onValueChange={(v) => setActiveSubTab(v as any)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="vitals" className="gap-2">
              <Activity className="w-4 h-4" />
              Vital Signs
              {vitalSigns.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{vitalSigns.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="systems" className="gap-2">
              <Stethoscope className="w-4 h-4" />
              System Exam
              {systemExams.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{systemExams.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="investigations" className="gap-2">
              <FileText className="w-4 h-4" />
              Summary
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vitals" className="mt-4">
            {vitalSigns.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vitalSigns.map((assessment, idx) => (
                  <Card key={idx} className="p-4 bg-gradient-to-br from-primary/5 to-primary/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-primary" />
                      <h4 className="font-medium text-sm">{getAssessmentDisplayName(assessment.type, idx)}</h4>
                    </div>
                    <p className="text-lg font-semibold text-foreground">{assessment.findings}</p>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center border-dashed">
                <Activity className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">Vital signs will be available during the simulation</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="systems" className="mt-4">
            {systemExams.length > 0 ? (
              <div className="space-y-3">
                {systemExams.map((assessment, idx) => (
                  <Card 
                    key={idx} 
                    className="overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => toggleSystem(assessment.type)}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          {getSystemIcon(assessment.type)}
                        </div>
                        <div>
                          <h4 className="font-medium">{getAssessmentDisplayName(assessment.type, idx)}</h4>
                          {!expandedSystems.includes(assessment.type) && (
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {stripHtml(assessment.findings).substring(0, 60)}...
                            </p>
                          )}
                        </div>
                      </div>
                      {expandedSystems.includes(assessment.type) ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    {expandedSystems.includes(assessment.type) && (
                      <div className="px-4 pb-4 pt-0 border-t">
                        <div 
                          className="prose prose-sm max-w-none dark:prose-invert mt-3"
                          dangerouslySetInnerHTML={{ __html: renderHtml(assessment.findings) }}
                        />
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center border-dashed">
                <Stethoscope className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">System examination findings will be revealed as you conduct the assessment</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="investigations" className="mt-4">
            <Card className="p-6">
              <h4 className="font-medium mb-4">Assessment Summary</h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Vital Signs Recorded</span>
                  <Badge variant={vitalSigns.length > 0 ? "default" : "secondary"}>
                    {vitalSigns.length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Systems Examined</span>
                  <Badge variant={systemExams.length > 0 ? "default" : "secondary"}>
                    {systemExams.length}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm">Total Findings</span>
                  <Badge variant="outline">
                    {(assessments?.length || 0)}
                  </Badge>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-4 border-dashed text-center">
            <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-sm">Vital Signs</h4>
            <p className="text-xs text-muted-foreground mt-1">Check during simulation</p>
          </Card>
          <Card className="p-4 border-dashed text-center">
            <Stethoscope className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-sm">System Exam</h4>
            <p className="text-xs text-muted-foreground mt-1">Perform as needed</p>
          </Card>
          <Card className="p-4 border-dashed text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <h4 className="font-medium text-sm">Investigations</h4>
            <p className="text-xs text-muted-foreground mt-1">Order if required</p>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============================================
// DIAGNOSTICS TAB (NEW)
// ============================================
export function DiagnosticsTab({ 
  tests,
  afterConsultation = false
}: { 
  tests?: DiagnosticTest[];
  afterConsultation?: boolean;
}) {
  const [expandedTest, setExpandedTest] = useState<string | null>(null);

  const hasTests = tests && tests.length > 0;

  // Group tests by category
  const bloodTests = tests?.filter(t => 
    t.name.toLowerCase().includes('blood') || 
    t.name.toLowerCase().includes('fbc') ||
    t.name.toLowerCase().includes('cbc') ||
    t.name.toLowerCase().includes('lipid') ||
    t.name.toLowerCase().includes('glucose') ||
    t.name.toLowerCase().includes('hba1c') ||
    t.name.toLowerCase().includes('liver') ||
    t.name.toLowerCase().includes('renal') ||
    t.name.toLowerCase().includes('electrolyte')
  ) || [];

  const imagingTests = tests?.filter(t => 
    t.name.toLowerCase().includes('x-ray') || 
    t.name.toLowerCase().includes('ct') ||
    t.name.toLowerCase().includes('mri') ||
    t.name.toLowerCase().includes('ultrasound') ||
    t.name.toLowerCase().includes('ecg') ||
    t.name.toLowerCase().includes('ekg') ||
    t.name.toLowerCase().includes('echo')
  ) || [];

  const otherTests = tests?.filter(t => 
    !bloodTests.includes(t) && !imagingTests.includes(t)
  ) || [];

  const renderTestCard = (test: DiagnosticTest, idx: number) => (
    <Card 
      key={idx} 
      className={cn(
        "overflow-hidden transition-all",
        expandedTest === test.name && "ring-2 ring-primary"
      )}
    >
      <div 
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpandedTest(expandedTest === test.name ? null : test.name)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <TestTube className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="font-medium">{test.name}</h4>
              {test.testDate && (
                <p className="text-xs text-muted-foreground">Date: {test.testDate}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {test.fileUrl && (
              <a 
                href={test.fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-primary" />
              </a>
            )}
            {expandedTest === test.name ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
      
      {expandedTest === test.name && (
        <div className="px-4 pb-4 border-t pt-4 space-y-3">
          {test.description && (
            <div>
              <h5 className="text-sm font-medium text-muted-foreground mb-1">Description</h5>
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: renderHtml(test.description) }}
              />
            </div>
          )}
          {test.results && (
            <div>
              <h5 className="text-sm font-medium text-muted-foreground mb-1">Results</h5>
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: renderHtml(test.results) }}
              />
            </div>
          )}
          {test.value && (
            <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
              <div>
                <span className="text-sm text-muted-foreground">Value:</span>
                <p className="font-semibold">{test.value} {test.unit}</p>
              </div>
              {test.referenceRange && (
                <div>
                  <span className="text-sm text-muted-foreground">Reference:</span>
                  <p className="text-sm">{test.referenceRange}</p>
                </div>
              )}
            </div>
          )}
          {/* Display image inline for imaging tests (ECG, X-ray, etc.) */}
          {test.fileUrl && (
            <div className="space-y-3">
              {/* Check if it's an image file */}
              {(test.fileUrl.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) || 
                test.name.toLowerCase().includes('ecg') || 
                test.name.toLowerCase().includes('x-ray') ||
                test.name.toLowerCase().includes('ct') ||
                test.name.toLowerCase().includes('mri')) && (
                <div className="rounded-lg overflow-hidden border bg-muted/30">
                  <a href={test.fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                    <img 
                      src={test.fileUrl} 
                      alt={test.name}
                      className="w-full max-h-[500px] object-contain hover:opacity-90 transition-opacity cursor-zoom-in"
                      onError={(e) => {
                        // If image fails to load, hide the img element
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </a>
                  <p className="text-xs text-center text-muted-foreground py-2">Click image to view full size</p>
                </div>
              )}
              <a 
                href={test.fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 rounded-lg text-primary transition-colors"
              >
                <Download className="w-4 h-4" />
                View/Download Report
              </a>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <TestTube className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-xl font-semibold">Diagnostic Tests</h3>
        {afterConsultation && (
          <Badge variant="outline" className="text-amber-600 border-amber-300">
            Revealed after consultation
          </Badge>
        )}
      </div>

      {hasTests ? (
        <div className="space-y-6">
          {/* Blood Tests */}
          {bloodTests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Blood Tests ({bloodTests.length})
              </h4>
              <div className="space-y-3">
                {bloodTests.map((test, idx) => renderTestCard(test, idx))}
              </div>
            </div>
          )}

          {/* Imaging/ECG */}
          {imagingTests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Imaging & ECG ({imagingTests.length})
              </h4>
              <div className="space-y-3">
                {imagingTests.map((test, idx) => renderTestCard(test, idx))}
              </div>
            </div>
          )}

          {/* Other Tests */}
          {otherTests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Other Tests ({otherTests.length})
              </h4>
              <div className="space-y-3">
                {otherTests.map((test, idx) => renderTestCard(test, idx))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <Card className="p-8 text-center border-dashed">
          <TestTube className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <h4 className="font-medium mb-2">No Diagnostic Tests Available</h4>
          <p className="text-sm text-muted-foreground">
            Order investigations during the consultation to receive results
          </p>
        </Card>
      )}

      {/* Tip */}
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <p className="text-sm text-purple-800 dark:text-purple-200">
          🔬 <strong>Tip:</strong> Review available test results carefully and consider how they relate to the patient's presenting complaint and history.
        </p>
      </div>
    </div>
  );
}
